import { useMemo, useState, useCallback } from 'react'
import Calendar from 'react-calendar'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  IoTimeOutline,
  IoCalendarOutline,
  IoAddCircleOutline,
  IoReloadOutline,
  IoPencilOutline,
  IoTrashOutline,
  IoListOutline,
  IoGridOutline,
  IoCalendarNumberOutline,
  IoShareOutline,
  IoWarningOutline,
  IoChevronBackOutline,
  IoChevronForwardOutline,
  IoRepeatOutline,
  IoCheckmarkCircleOutline,
  IoEllipseOutline,
  IoPlayCircleOutline,
} from 'react-icons/io5'
import { useAppointments, useDeleteAppointment } from '../hooks/useAppointments'
import type { Appointment } from '../types'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import toast from 'react-hot-toast'

/* ─── Helpers ─── */
const pad2 = (n: number) => String(n).padStart(2, '0')
const formatKey = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
const formatTime = (s: string) => new Date(s).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
const formatDateLong = (d: Date) => d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
const formatDateShort = (d: Date) => d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' })
const DAYS_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']

type ViewMode = 'month' | 'week' | 'list'

/* ─── Status config ─── */
const statusCfg: Record<string, { label: string; dot: string; icon: React.ElementType }> = {
  PENDING: { label: 'En attente', dot: 'bg-gray-400', icon: IoEllipseOutline },
  IN_PROGRESS: { label: 'En cours', dot: 'bg-blue-400 animate-pulse', icon: IoPlayCircleOutline },
  COMPLETED: { label: 'Terminé', dot: 'bg-emerald-400', icon: IoCheckmarkCircleOutline },
  SKIPPED: { label: 'Passé', dot: 'bg-amber-400', icon: IoWarningOutline },
  CANCELLED: { label: 'Annulé', dot: 'bg-red-400', icon: IoEllipseOutline },
}

/* ─── Détection conflits ─── */
function findConflicts(appts: Appointment[]): Set<number> {
  const conflictIds = new Set<number>()
  const sorted = [...appts].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i]!, b = sorted[i + 1]!
    if (new Date(a.endDate).getTime() > new Date(b.startDate).getTime()) {
      conflictIds.add(a.id); conflictIds.add(b.id)
    }
  }
  return conflictIds
}

/* ─── Export iCal ─── */
function exportIcal(appointments: Appointment[]) {
  const esc = (s: string) => s.replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;')
  const fmtDt = (s: string) => new Date(s).toISOString().replace(/[-:]/g, '').replace('.000', '')

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//MediSync//Planning//FR',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    ...appointments.flatMap(a => [
      'BEGIN:VEVENT',
      `UID:medisync-${a.id}@medisync.fr`,
      `DTSTAMP:${fmtDt(new Date().toISOString())}`,
      `DTSTART:${fmtDt(a.startDate)}`,
      `DTEND:${fmtDt(a.endDate)}`,
      `SUMMARY:${a.patient ? esc(`${a.patient.lastName} ${a.patient.firstName}`) : 'Patient inconnu'}`,
      a.notes ? `DESCRIPTION:${esc(a.notes)}` : '',
      a.patient?.adresse ? `LOCATION:${esc(a.patient.adresse)}` : '',
      'END:VEVENT',
    ].filter(Boolean)),
    'END:VCALENDAR',
  ]

  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = 'medisync-planning.ics'; a.click()
  URL.revokeObjectURL(url)
  toast.success('Planning exporté (.ics)')
}

/* ─── Carte rendez-vous ─── */
function AppointmentCard({
  appointment,
  hasConflict,
  onEdit,
  onDelete,
  deleting,
}: {
  appointment: Appointment
  hasConflict: boolean
  onEdit: () => void
  onDelete: () => void
  deleting: boolean
}) {
  const s = statusCfg[appointment.status ?? 'PENDING'] ?? statusCfg['PENDING']!
  return (
    <div className={`rounded-xl p-3 border transition-colors ${
      hasConflict
        ? 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30'
        : 'bg-gray-50 dark:bg-dark-100 border-gray-100 dark:border-gray-700/50 hover:border-primary-500/30'
    }`}>
      <div className='flex items-start justify-between gap-2'>
        <div className='flex-1 min-w-0'>
          <div className='flex items-center gap-1.5 mb-0.5'>
            {hasConflict && <IoWarningOutline className='text-red-500 flex-shrink-0 text-xs' />}
            <p className='font-semibold text-gray-900 dark:text-gray-100 text-sm truncate'>
              {appointment.patient
                ? `${appointment.patient.lastName.toUpperCase()} ${appointment.patient.firstName}`
                : 'Patient inconnu'}
            </p>
          </div>
          <div className='flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 flex-wrap'>
            <span className='flex items-center gap-1'>
              <IoTimeOutline className='text-primary-400 flex-shrink-0' />
              {formatTime(appointment.startDate)} – {formatTime(appointment.endDate)}
            </span>
            <span className='flex items-center gap-1.5'>
              <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
              {s.label}
            </span>
            {appointment.recurrenceRule && (
              <span className='flex items-center gap-1 text-primary-500'>
                <IoRepeatOutline className='text-xs' />Récurrent
              </span>
            )}
          </div>
          {appointment.notes && (
            <p className='text-xs text-gray-400 mt-1 italic truncate'>{appointment.notes}</p>
          )}
          {appointment.patient?.adresse && (
            <p className='text-xs text-gray-400 mt-0.5 truncate'>📍 {appointment.patient.adresse}</p>
          )}
        </div>
        <div className='flex gap-1 flex-shrink-0'>
          <button onClick={onEdit}
            className='p-1.5 rounded-lg text-gray-400 hover:text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-500/10 transition-colors'>
            <IoPencilOutline className='text-sm' />
          </button>
          <button onClick={onDelete} disabled={deleting}
            className='p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-40'>
            <IoTrashOutline className='text-sm' />
          </button>
        </div>
      </div>
    </div>
  )
}

/* ─── Vue Semaine ─── */
function WeekView({
  weekStart,
  eventsByDate,
  onSelectDate,
  conflictIds,
}: {
  weekStart: Date
  eventsByDate: Record<string, Appointment[]>
  onSelectDate: (d: Date) => void
  conflictIds: Set<number>
  onEdit: (a: Appointment) => void
  onDelete: (id: number) => void
  deleting: boolean
}) {
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); return d
  })
  const todayKey = formatKey(new Date())

  return (
    <div className='grid grid-cols-7 gap-1'>
      {days.map(day => {
        const key = formatKey(day)
        const appts = eventsByDate[key] ?? []
        const isToday = key === todayKey
        return (
          <div key={key} className='min-h-24'>
            <button onClick={() => onSelectDate(day)}
              className={`w-full text-center py-2 rounded-xl mb-1 transition-all ${
                isToday ? 'bg-primary-500 text-white font-bold shadow-sm' : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
              }`}>
              <div className='text-xs opacity-70'>{DAYS_FR[day.getDay()]}</div>
              <div className={`text-sm font-bold ${isToday ? '' : ''}`}>{day.getDate()}</div>
            </button>
            <div className='space-y-0.5'>
              {appts.slice(0, 3).map(a => (
                <div key={a.id}
                  className={`text-xs px-1.5 py-0.5 rounded-lg truncate font-medium cursor-pointer ${
                    conflictIds.has(a.id)
                      ? 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400'
                      : 'bg-primary-100 dark:bg-primary-500/20 text-primary-700 dark:text-primary-300'
                  }`}
                  title={`${formatTime(a.startDate)} — ${a.patient ? `${a.patient.lastName} ${a.patient.firstName}` : 'Patient inconnu'}`}>
                  {formatTime(a.startDate)} {a.patient?.lastName ?? '?'}
                </div>
              ))}
              {appts.length > 3 && (
                <div className='text-xs text-gray-400 text-center'>+{appts.length - 3}</div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/* ─── Vue Liste ─── */
function ListView({
  appointments,
  conflictIds,
  onEdit,
  onDelete,
  deleting,
}: {
  appointments: Appointment[]
  conflictIds: Set<number>
  onEdit: (a: Appointment) => void
  onDelete: (id: number) => void
  deleting: boolean
}) {
  const grouped = useMemo(() => {
    const map = new Map<string, Appointment[]>()
    const sorted = [...appointments].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
    sorted.forEach(a => {
      const key = formatKey(new Date(a.startDate))
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(a)
    })
    return Array.from(map.entries()).slice(0, 60)
  }, [appointments])

  if (grouped.length === 0) return (
    <div className='text-center py-16 text-gray-400'>
      <IoCalendarOutline size={36} className='mx-auto mb-3 opacity-50' />
      <p className='text-sm'>Aucun rendez-vous</p>
    </div>
  )

  return (
    <div className='space-y-4 max-h-[600px] overflow-y-auto pr-1'>
      {grouped.map(([key, appts]) => {
        const d = new Date(key)
        const isToday = key === formatKey(new Date())
        return (
          <div key={key}>
            <div className={`flex items-center gap-2 mb-2 ${isToday ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}`}>
              <span className={`text-xs font-bold uppercase tracking-wider ${isToday ? 'text-primary-600 dark:text-primary-400' : ''}`}>
                {isToday ? "Aujourd'hui — " : ''}{formatDateShort(d)}
              </span>
              <span className='flex-1 h-px bg-gray-100 dark:bg-gray-800' />
              <span className='text-xs font-semibold'>{appts.length}</span>
            </div>
            <div className='space-y-2 pl-2'>
              {appts.map(a => (
                <AppointmentCard key={a.id} appointment={a} hasConflict={conflictIds.has(a.id)}
                  onEdit={() => onEdit(a)} onDelete={() => onDelete(a.id)} deleting={deleting} />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/* ═══════════════════════════════════════
   COMPOSANT PRINCIPAL
═══════════════════════════════════════ */
export default function Planning() {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [viewMode, setViewMode] = useState<ViewMode>('month')
  const [weekStart, setWeekStart] = useState<Date>(() => {
    const d = new Date(); const day = d.getDay()
    d.setDate(d.getDate() - (day === 0 ? 6 : day - 1)); d.setHours(0, 0, 0, 0); return d
  })
  const navigate = useNavigate()

  const { data: appointmentsData, isLoading, error, refetch } = useAppointments({ limit: 1000, sortOrder: 'ASC' })
  const deleteAppointment = useDeleteAppointment()

  const handleDelete = async (id: number) => {
    if (!window.confirm('Supprimer ce rendez-vous ?')) return
    await deleteAppointment.mutateAsync(id)
  }

  const appointments = useMemo(() => appointmentsData?.data ?? [], [appointmentsData])

  const eventsByDate = useMemo(() => {
    return appointments.reduce<Record<string, Appointment[]>>((acc, a) => {
      const key = formatKey(new Date(a.startDate))
      if (!acc[key]) acc[key] = []
      acc[key]!.push(a)
      return acc
    }, {})
  }, [appointments])

  const conflictIds = useMemo(() => findConflicts(appointments), [appointments])

  const selectedDateKey = formatKey(selectedDate)
  const dayAppointments = eventsByDate[selectedDateKey] ?? []
  const todayCount = eventsByDate[formatKey(new Date())]?.length ?? 0
  const conflictsCount = conflictIds.size / 2

  const prevWeek = useCallback(() => {
    const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d)
  }, [weekStart])
  const nextWeek = useCallback(() => {
    const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d)
  }, [weekStart])
  const weekEnd = new Date(weekStart); weekEnd.setDate(weekEnd.getDate() + 6)

  return (
    <div className='min-h-full bg-gray-50 dark:bg-dark-200'>
      <div className='p-4 lg:p-6 max-w-7xl mx-auto'>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} className='flex items-start justify-between flex-wrap gap-3 mb-6'>
          <div>
            <h1 className='text-2xl font-bold text-gray-900 dark:text-gray-100'>Planning</h1>
            <p className='text-gray-500 dark:text-gray-400 text-sm mt-0.5'>Gérez vos rendez-vous</p>
          </div>
          <div className='flex items-center gap-2 flex-wrap'>
            {/* Vues */}
            <div className='flex bg-white dark:bg-dark-50 rounded-xl border border-gray-200 dark:border-gray-700 p-0.5'>
              {([['month', IoCalendarOutline, 'Mois'], ['week', IoGridOutline, 'Semaine'], ['list', IoListOutline, 'Liste']] as const).map(([v, Icon, label]) => (
                <button key={v} onClick={() => setViewMode(v)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    viewMode === v ? 'bg-primary-500 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}>
                  <Icon size={14} /><span className='hidden sm:inline'>{label}</span>
                </button>
              ))}
            </div>

            {/* Export iCal */}
            <button onClick={() => exportIcal(appointments)}
              className='flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white dark:bg-dark-50 border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-600 dark:text-gray-300 hover:border-primary-400 hover:text-primary-600 transition-all'>
              <IoShareOutline size={15} /><span className='hidden sm:inline'>Exporter .ics</span>
            </button>

            {/* Nouveau RDV */}
            <button onClick={() => navigate(`/add-appointment?date=${formatKey(selectedDate)}`)}
              className='flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold transition-colors shadow-sm'>
              <IoAddCircleOutline size={16} />Nouveau RDV
            </button>
          </div>
        </motion.div>

        {/* Stats rapides */}
        <div className='grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6'>
          {[
            { icon: IoCalendarOutline, label: 'Total', value: appointments.length, color: 'primary' },
            { icon: IoTimeOutline, label: "Aujourd'hui", value: todayCount, color: 'success' },
            { icon: IoCalendarNumberOutline, label: 'Sélectionné', value: dayAppointments.length, color: 'warning' },
            { icon: IoWarningOutline, label: 'Conflits', value: Math.round(conflictsCount), color: 'danger' },
          ].map(({ icon: Icon, label, value, color }, i) => {
            const colorClass: Record<string, string> = {
              primary: 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-500/10 border-primary-100 dark:border-primary-500/20',
              success: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 border-emerald-100 dark:border-emerald-500/20',
              warning: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 border-amber-100 dark:border-amber-500/20',
              danger: value > 0 ? 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 border-red-100 dark:border-red-500/20' : 'text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700',
            }
            return (
              <motion.div key={label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                className={`rounded-2xl p-4 border flex items-center gap-3 ${colorClass[color]}`}>
                <Icon size={22} className='flex-shrink-0' />
                <div>
                  <div className='text-2xl font-bold leading-none'>{value}</div>
                  <div className='text-xs mt-0.5 opacity-70'>{label}</div>
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* Alerte conflits */}
        <AnimatePresence>
          {conflictIds.size > 0 && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className='mb-4 flex items-center gap-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-2xl px-4 py-3'>
              <IoWarningOutline className='text-red-500 text-xl flex-shrink-0' />
              <div className='flex-1'>
                <p className='text-sm font-semibold text-red-700 dark:text-red-400'>
                  {Math.round(conflictIds.size / 2)} conflit{conflictIds.size / 2 > 1 ? 's' : ''} horaire{conflictIds.size / 2 > 1 ? 's' : ''} détecté{conflictIds.size / 2 > 1 ? 's' : ''}
                </p>
                <p className='text-xs text-red-600 dark:text-red-400 opacity-80'>Des rendez-vous se chevauchent dans votre planning</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Contenu selon vue */}
        <AnimatePresence mode='wait'>
          {viewMode === 'list' ? (
            <motion.div key='list' initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className='bg-white dark:bg-dark-50 rounded-2xl p-5 border border-gray-200 dark:border-gray-800'>
              {isLoading ? (
                <div className='flex justify-center py-12'><LoadingSpinner /></div>
              ) : (
                <ListView appointments={appointments} conflictIds={conflictIds}
                  onEdit={a => navigate(`/appointments/${a.id}/edit`)}
                  onDelete={id => void handleDelete(id)}
                  deleting={deleteAppointment.isPending} />
              )}
            </motion.div>
          ) : viewMode === 'week' ? (
            <motion.div key='week' initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className='bg-white dark:bg-dark-50 rounded-2xl p-5 border border-gray-200 dark:border-gray-800'>
              {/* Header semaine */}
              <div className='flex items-center justify-between mb-4'>
                <button onClick={prevWeek} className='p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'>
                  <IoChevronBackOutline className='text-gray-500' />
                </button>
                <span className='text-sm font-semibold text-gray-800 dark:text-gray-100'>
                  {weekStart.getDate()} {MONTHS_FR[weekStart.getMonth()]?.slice(0, 4)} – {weekEnd.getDate()} {MONTHS_FR[weekEnd.getMonth()]?.slice(0, 4)} {weekEnd.getFullYear()}
                </span>
                <button onClick={nextWeek} className='p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'>
                  <IoChevronForwardOutline className='text-gray-500' />
                </button>
              </div>
              {isLoading ? (
                <div className='flex justify-center py-12'><LoadingSpinner /></div>
              ) : (
                <>
                  <WeekView weekStart={weekStart} eventsByDate={eventsByDate}
                    onSelectDate={d => { setSelectedDate(d); setViewMode('month') }}
                    conflictIds={conflictIds}
                    onEdit={a => navigate(`/appointments/${a.id}/edit`)}
                    onDelete={id => void handleDelete(id)}
                    deleting={deleteAppointment.isPending} />
                  {/* Détail semaine : liste sous la grille */}
                  <div className='mt-5 border-t border-gray-100 dark:border-gray-800 pt-4 space-y-2'>
                    {Array.from({ length: 7 }, (_, i) => {
                      const d = new Date(weekStart); d.setDate(d.getDate() + i)
                      return { date: d, appts: eventsByDate[formatKey(d)] ?? [] }
                    })
                      .filter(x => x.appts.length > 0)
                      .flatMap(({ appts }) => appts)
                      .map(a => (
                        <AppointmentCard key={a.id} appointment={a} hasConflict={conflictIds.has(a.id)}
                          onEdit={() => navigate(`/appointments/${a.id}/edit`)}
                          onDelete={() => void handleDelete(a.id)}
                          deleting={deleteAppointment.isPending} />
                      ))}
                  </div>
                </>
              )}
            </motion.div>
          ) : (
            /* Vue Mois (défaut) */
            <motion.div key='month' initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className='grid grid-cols-1 lg:grid-cols-2 gap-4'>

              {/* Calendrier */}
              <div className='bg-white dark:bg-dark-50 rounded-2xl p-5 border border-gray-200 dark:border-gray-800'>
                <div className='flex items-center gap-2 mb-4'>
                  <div className='bg-primary-500/10 p-2 rounded-lg'>
                    <IoCalendarOutline className='text-primary-400 text-lg' />
                  </div>
                  <h2 className='text-base font-bold text-gray-900 dark:text-gray-100'>Calendrier</h2>
                </div>
                <Calendar
                  onChange={value => {
                    const date = value instanceof Date ? value : Array.isArray(value) && value[0] instanceof Date ? value[0] : null
                    if (date) setSelectedDate(date)
                  }}
                  value={selectedDate}
                  className='!w-full !bg-transparent !border-0'
                  tileClassName={({ date, view }) => {
                    if (view !== 'month') return ''
                    const key = formatKey(date)
                    const isSelected = key === formatKey(selectedDate)
                    const hasEvent = (eventsByDate[key]?.length ?? 0) > 0
                    const hasConflict = eventsByDate[key]?.some(a => conflictIds.has(a.id)) ?? false
                    const isToday = key === formatKey(new Date())
                    return `!rounded-lg !p-2 transition-all ${
                      isSelected ? '!bg-primary-600 !text-white !font-bold !shadow-lg'
                        : hasConflict ? '!bg-red-100 dark:!bg-red-500/20 !text-red-600 dark:!text-red-400 !font-semibold'
                          : hasEvent ? '!bg-primary-500/20 !text-primary-600 dark:!text-primary-400 !font-semibold'
                            : isToday ? '!bg-primary-600/10 !text-primary-600 dark:!text-primary-300'
                              : '!text-gray-900 dark:!text-white hover:!bg-gray-100 dark:hover:!bg-gray-700'
                    }`
                  }}
                  formatShortWeekday={(_locale, date) => DAYS_FR[date.getDay()] ?? ''}
                  nextLabel='›' prevLabel='‹' next2Label={null} prev2Label={null}
                />
              </div>

              {/* Rendez-vous du jour */}
              <div className='bg-white dark:bg-dark-50 rounded-2xl p-5 border border-gray-200 dark:border-gray-800'>
                <div className='flex items-center justify-between mb-4'>
                  <div className='flex items-center gap-2'>
                    <div className='bg-success-500/10 p-2 rounded-lg'>
                      <IoTimeOutline className='text-success-600 text-lg' />
                    </div>
                    <div>
                      <h2 className='text-base font-bold text-gray-900 dark:text-gray-100'>Rendez-vous</h2>
                      <p className='text-xs text-gray-500 dark:text-gray-400 capitalize'>{formatDateLong(selectedDate)}</p>
                    </div>
                  </div>
                </div>

                {isLoading ? (
                  <div className='flex items-center justify-center py-12'><LoadingSpinner /></div>
                ) : error ? (
                  <div className='text-center py-12'>
                    <IoReloadOutline className='text-red-500 text-4xl mx-auto mb-3' />
                    <p className='text-red-500 mb-4 text-sm'>Impossible de charger les rendez-vous</p>
                    <button onClick={() => void refetch()} className='btn-secondary text-sm'>
                      <IoReloadOutline className='inline mr-2' />Réessayer
                    </button>
                  </div>
                ) : dayAppointments.length === 0 ? (
                  <div className='text-center py-12'>
                    <IoCalendarOutline className='text-gray-400 text-4xl mx-auto mb-3' />
                    <p className='text-gray-500 dark:text-gray-400 mb-1 text-sm'>Aucun rendez-vous</p>
                    <p className='text-gray-400 text-xs mb-5'>pour cette date</p>
                    <button onClick={() => navigate(`/add-appointment?date=${formatKey(selectedDate)}`)} className='btn-primary'>
                      <IoAddCircleOutline className='inline mr-2' />Ajouter un rendez-vous
                    </button>
                  </div>
                ) : (
                  <div className='space-y-2 max-h-[400px] overflow-y-auto'>
                    {dayAppointments.map(a => (
                      <AppointmentCard key={a.id} appointment={a} hasConflict={conflictIds.has(a.id)}
                        onEdit={() => navigate(`/appointments/${a.id}/edit`)}
                        onDelete={() => void handleDelete(a.id)}
                        deleting={deleteAppointment.isPending} />
                    ))}
                  </div>
                )}

                {dayAppointments.length > 0 && (
                  <div className='mt-4'>
                    <button onClick={() => navigate(`/add-appointment?date=${formatKey(selectedDate)}`)} className='btn-primary w-full'>
                      <IoAddCircleOutline className='inline mr-2' />Nouveau rendez-vous
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
